<template>
  <EditableList
    :items="modelValue"
    add-label="Add component"
    remove-label="Remove component"
    @add="addComponent"
    @remove="removeComponent"
  >
    <template #row="{ item: component, index }">
      <div class="component-fields">
        <!-- Type selector always shown first -->
        <StField label="Type">
          <StSelect
            :model-value="component.contentType"
            @update:model-value="updateType(index, String($event))"
          >
            <option value="text">text</option>
            <option value="header">header</option>
            <option value="boxed">boxed</option>
            <option value="ability">ability</option>
            <option value="accordion">accordion</option>
          </StSelect>
        </StField>

        <!-- text / header / boxed: just a text textarea -->
        <template
          v-if="
            component.contentType === 'text' ||
            component.contentType === 'header' ||
            component.contentType === 'boxed'
          "
        >
          <StField class="field--full" label="Text">
            <StTextarea
              :model-value="component.text ?? ''"
              placeholder="Enter text…"
              @update:model-value="updateField(index, 'text', $event)"
            />
          </StField>
        </template>

        <!-- accordion: title + text -->
        <template v-else-if="component.contentType === 'accordion'">
          <StField label="Title">
            <StTextInput
              :model-value="component.title ?? ''"
              placeholder="Accordion title"
              @update:model-value="updateField(index, 'title', $event)"
            />
          </StField>
          <StField class="field--full" label="Text">
            <StTextarea
              :model-value="component.text ?? ''"
              placeholder="Accordion body text…"
              @update:model-value="updateField(index, 'text', $event)"
            />
          </StField>
        </template>

        <!-- ability: delegate to AbilityForm with a single-element array -->
        <template v-else-if="component.contentType === 'ability'">
          <div class="field--full">
            <AbilityForm
              :model-value="abilityArray(index)"
              @update:model-value="updateAbility(index, $event)"
            />
          </div>
        </template>
      </div>
    </template>
  </EditableList>
</template>

<script setup lang="ts">
import { AbilityType, fromAoSData, toAoSData, makeId, type IAbility } from '@scrolldex/shared';
import type RuleComponent from '@scrolldex/shared/src/rules/component.js';
import { StField, StTextInput, StTextarea, StSelect } from '../ui/index.js';
import EditableList from '../EditableList.vue';
import AbilityForm from '../AbilityForm.vue';

const props = defineProps<{
  modelValue: RuleComponent[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: RuleComponent[]): void;
}>();

function newAbility(): IAbility {
  return {
    id: makeId('ability'),
    name: '',
    type: AbilityType.Activated,
    superType: 'Ability',
    timing: '',
    keywords: [],
    effect: '',
    color: 'Black',
    typeAttribute: 'Special',
    declare: null,
    usedBy: null,
    cost: null,
    unlockCondition: null,
  };
}

function newComponent(type: RuleComponent['contentType']): RuleComponent {
  if (type === 'ability') {
    return { contentType: 'ability', ability: toAoSData(newAbility()) };
  }
  if (type === 'accordion') {
    return { contentType: 'accordion', title: '', text: '' };
  }
  return { contentType: type, text: '' };
}

function addComponent() {
  emit('update:modelValue', [...props.modelValue, newComponent('text')]);
}

function removeComponent(index: number) {
  emit(
    'update:modelValue',
    props.modelValue.filter((_, i) => i !== index),
  );
}

function updateField(index: number, field: keyof RuleComponent, value: unknown) {
  const updated = props.modelValue.map((component, i) => {
    if (i !== index) return component;
    return { ...component, [field]: value };
  });
  emit('update:modelValue', updated);
}

function updateType(index: number, type: string) {
  const updated = props.modelValue.map((component, i) => {
    if (i !== index) return component;
    return newComponent(type as RuleComponent['contentType']);
  });
  emit('update:modelValue', updated);
}

// Build a single-element IAbility array for AbilityForm from the component's
// raw IRuleAbility, converting via fromAoSData. Returns empty array if no
// ability is stored yet (shouldn't happen for 'ability' type, but guards
// against stale data).
function abilityArray(index: number): IAbility[] {
  const component = props.modelValue[index];
  if (!component.ability) return [];
  return [fromAoSData(component.ability)];
}

// When AbilityForm emits an updated single-element array, convert the IAbility
// back to IRuleAbility via toAoSData and store it on the component.
function updateAbility(index: number, abilities: IAbility[]) {
  const updated = props.modelValue.map((component, i) => {
    if (i !== index) return component;
    return { ...component, ability: abilities[0] ? toAoSData(abilities[0]) : component.ability };
  });
  emit('update:modelValue', updated);
}
</script>

<style scoped>
.component-fields {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--st-space-2);
  align-items: end;
}

.component-fields :deep(.field--full) {
  grid-column: 1 / -1;
}
</style>
